import { PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import type { FeeLine, PaymentMethod, Receipt } from "@/types";
import { getCurrency, type Currency } from "./currency";

/** Ligne de situation au format du reçu. */
export function toFeeSnapshot(line: FeeLine): FeeSnapshot {
  return { key: line.key, label: line.label, due: line.due, paid: line.paid };
}

/* -------------------------------------------------------------------------- */
/* Montant en lettres (français)                                               */
/* -------------------------------------------------------------------------- */

const UNITS = [
  "zéro", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf",
  "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize",
  "dix-sept", "dix-huit", "dix-neuf",
];
const TENS = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante", "quatre-vingt", "quatre-vingt"];

function below100(n: number): string {
  if (n < 20) return UNITS[n];
  const ten = Math.floor(n / 10);
  const unit = n % 10;
  if (ten === 7) {
    if (unit === 1) return "soixante et onze";
    return `soixante-${UNITS[10 + unit]}`;
  }
  if (ten === 9) return `quatre-vingt-${UNITS[10 + unit]}`;
  if (ten === 8) return unit === 0 ? "quatre-vingts" : `quatre-vingt-${UNITS[unit]}`;
  if (unit === 0) return TENS[ten];
  if (unit === 1) return `${TENS[ten]} et un`;
  return `${TENS[ten]}-${UNITS[unit]}`;
}

function below1000(n: number): string {
  const hundred = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (hundred > 0) {
    if (hundred === 1) parts.push("cent");
    else parts.push(`${UNITS[hundred]} cent${rest === 0 ? "s" : ""}`);
  }
  if (rest > 0) parts.push(below100(rest));
  return parts.join(" ");
}

/** Convertit un montant en toutes lettres dans la devise de l'école, ex. 150000 -> « cent cinquante mille francs guinéens ». */
export function montantEnLettres(amount: number, currency: Currency = getCurrency()): string {
  const abs = Math.round(Math.abs(amount));
  const { name, plural: pluralName } = currency;
  if (abs === 0) return `zéro ${name}`;
  let n = abs;
  const parts: string[] = [];
  const billions = Math.floor(n / 1_000_000_000);
  n %= 1_000_000_000;
  const millions = Math.floor(n / 1_000_000);
  n %= 1_000_000;
  const thousands = Math.floor(n / 1000);
  n %= 1000;
  const rest = n;
  if (billions > 0) parts.push(`${below1000(billions)} milliard${billions > 1 ? "s" : ""}`);
  if (millions > 0) parts.push(`${below1000(millions)} million${millions > 1 ? "s" : ""}`);
  if (thousands > 0) parts.push(thousands === 1 ? "mille" : `${below1000(thousands)} mille`);
  if (rest > 0) parts.push(below1000(rest));
  const words = parts.join(" ");
  const plural = abs > 1;
  // « un million de francs » : « de » après un nombre rond de millions ou de milliards.
  const de = rest === 0 && thousands === 0 && (millions > 0 || billions > 0) ? "de " : "";
  return `${words} ${de}${plural ? pluralName : name}`;
}

/* -------------------------------------------------------------------------- */
/* Reçu                                                                        */
/* -------------------------------------------------------------------------- */

export interface FeeSnapshot {
  key: string;
  label: string;
  due: number;
  paid: number;
}

export interface ReceiptFeeLine {
  label: string;
  due: number;
  paid: number;
  remaining: number;
  current: boolean;
}

export interface ReceiptVersementLine {
  receiptNo: string;
  date: string;
  label: string;
  method: string;
  amount: number;
}

export interface ReceiptStudent {
  name: string;
  matricule: string;
  className: string;
  cycleLabel: string;
  schoolYear: string;
}

export interface ReceiptData {
  title: string;
  receiptNo: string;
  date: string;
  method: string;
  note: string;
  /** Avertissement bien visible (reçu provisoire). */
  notice?: string;
  student: ReceiptStudent;
  amountReceived: number;
  lines: ReceiptFeeLine[];
  versements: ReceiptVersementLine[];
  totalDue: number;
  totalPaid: number;
  remaining: number;
}

function toFeeLines(fees: FeeSnapshot[], currentKey?: string): ReceiptFeeLine[] {
  return fees.map((f) => ({
    label: f.label,
    due: f.due,
    paid: f.paid,
    remaining: Math.max(0, f.due - f.paid),
    current: currentKey ? f.key === currentKey : false,
  }));
}

function totalsOf(fees: FeeSnapshot[]) {
  const totalDue = fees.reduce((s, f) => s + f.due, 0);
  const totalPaid = fees.reduce((s, f) => s + f.paid, 0);
  return { totalDue, totalPaid, remaining: Math.max(0, totalDue - totalPaid) };
}

/**
 * Reçu provisoire d'un paiement noté hors ligne : prouve le versement au parent en attendant le reçu officiel,
 * dont le numéro ne peut venir que du serveur. `amounts` : montant versé par frais (clé de ligne).
 */
export function buildProvisionalReceipt(args: {
  ref: string;
  date: string;
  method: PaymentMethod;
  note?: string;
  student: ReceiptStudent;
  fees: FeeSnapshot[];
  amounts: Record<string, number>;
}): ReceiptData {
  const fees = args.fees.map((f) => ({ ...f, paid: f.paid + (args.amounts[f.key] ?? 0) }));
  const method = PAYMENT_METHOD_LABEL[args.method];
  const settled = args.fees.filter((f) => (args.amounts[f.key] ?? 0) > 0);
  return {
    title: "Reçu provisoire",
    receiptNo: args.ref,
    date: args.date,
    method,
    note: args.note ?? "",
    notice: `Paiement en attente d'envoi au serveur (saisi sans connexion). Ce reçu sera remplacé par le reçu officiel numéroté : gardez-le jusque-là.`,
    student: args.student,
    amountReceived: settled.reduce((s, f) => s + (args.amounts[f.key] ?? 0), 0),
    lines: toFeeLines(fees).map((l, i) => ({ ...l, current: (args.amounts[fees[i].key] ?? 0) > 0 })),
    versements: settled.map((f) => ({
      receiptNo: args.ref,
      date: args.date,
      label: f.label,
      method,
      amount: args.amounts[f.key] ?? 0,
    })),
    ...totalsOf(fees),
  };
}

/** Reçu d'un versement : les lignes qu'il règle sont mises en évidence dans la situation de l'élève. */
export function buildVersementReceipt(args: {
  receipt: Receipt;
  fees: FeeSnapshot[];
  student: ReceiptStudent;
}): ReceiptData {
  const totals = totalsOf(args.fees);
  const settled = new Set(args.receipt.lines.map((l) => l.feeKey));
  return {
    title: args.receipt.cancelledAt ? "Reçu de paiement — ANNULÉ" : "Reçu de paiement",
    receiptNo: args.receipt.receiptNo,
    date: args.receipt.date,
    method: PAYMENT_METHOD_LABEL[args.receipt.method],
    note: args.receipt.cancelledAt
      ? `Annulé : ${args.receipt.cancelReason}`
      : args.receipt.note,
    student: args.student,
    amountReceived: args.receipt.total,
    lines: args.fees.map((f) => ({
      label: f.label,
      due: f.due,
      paid: f.paid,
      remaining: Math.max(0, f.due - f.paid),
      current: settled.has(f.key),
    })),
    versements: args.receipt.lines.map((l) => ({
      receiptNo: args.receipt.receiptNo,
      date: args.receipt.date,
      label: l.label,
      method: PAYMENT_METHOD_LABEL[args.receipt.method],
      amount: l.amount,
    })),
    ...totals,
  };
}

/** Reçu consolidé d'une inscription : situation des frais + tous les versements (reçus valides). */
export function buildEnrollmentReceipt(args: {
  fees: FeeSnapshot[];
  receipts: Receipt[];
  student: ReceiptStudent;
  title?: string;
}): ReceiptData {
  const totals = totalsOf(args.fees);
  const sorted = args.receipts.filter((r) => !r.cancelledAt).sort((a, b) => a.date.localeCompare(b.date));
  return {
    title: args.title ?? "Reçu de paiement",
    receiptNo: sorted[0]?.receiptNo ?? "—",
    date: sorted[sorted.length - 1]?.date ?? "",
    method: sorted.length > 1 ? "Versements multiples" : sorted[0] ? PAYMENT_METHOD_LABEL[sorted[0].method] : "—",
    note: "",
    student: args.student,
    amountReceived: totals.totalPaid,
    lines: toFeeLines(args.fees),
    versements: sorted.flatMap((r) =>
      r.lines.map((l) => ({
        receiptNo: r.receiptNo,
        date: r.date,
        label: l.label,
        method: PAYMENT_METHOD_LABEL[r.method],
        amount: l.amount,
      })),
    ),
    ...totals,
  };
}
